import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { BookOpen, ChevronRight, Plus } from 'lucide-react';
import { motion } from 'framer-motion';

interface ChapterOption {
  id: number;
  title: string;
  description?: string;
}

interface ChooseChapterStepProps {
  chapters: ChapterOption[];
  selectedChapterId: number | null;
  onSelectChapter: (id: number) => void;
  isCreatingNew: boolean;
  onToggleCreateNew: () => void;
  newChapterTitle: string;
  onNewChapterTitleChange: (title: string) => void;
  newChapterDescription: string;
  onNewChapterDescriptionChange: (desc: string) => void;
}

export function ChooseChapterStep({
  chapters,
  selectedChapterId,
  onSelectChapter,
  isCreatingNew,
  onToggleCreateNew,
  newChapterTitle,
  onNewChapterTitleChange,
  newChapterDescription,
  onNewChapterDescriptionChange,
}: ChooseChapterStepProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
      className="flex flex-col gap-5 w-full max-w-4xl mx-auto"
    >
      {/* Section: Choose or Create Chapter */}
      <div className="space-y-6 flex flex-col items-center">
        <div className="space-y-1.5 text-center">
          <h2 className="text-2xl font-bold tracking-tight text-foreground font-lora">Choose a Chapter</h2>
          <p className="text-muted-foreground text-sm max-w-sm">Select an existing chapter or create a new one to plan scenes for.</p>
        </div>

        {!isCreatingNew ? (
          <div className="space-y-4 flex flex-col w-full max-w-xl mx-auto">
            {chapters.length > 0 ? (
              <div className="grid gap-2.5 overflow-y-auto max-h-[400px] scrollbar-hide pr-1 -mr-1">
                {chapters.map((chapter) => (
                  <Card
                    key={chapter.id}
                    className={cn(
                      "flex items-start gap-4 p-5 cursor-pointer transition-all duration-200 border rounded-2xl h-fit",
                      selectedChapterId === chapter.id
                        ? "border-primary/50 bg-primary/5 shadow-sm ring-1 ring-primary/10"
                        : "border-border shadow-sm hover:border-primary/20 hover:bg-muted/5"
                    )}
                    onClick={() => onSelectChapter(chapter.id)}
                  >
                    <div className={cn(
                      "h-10 w-10 rounded-xl flex items-center justify-center transition-colors flex-shrink-0",
                      selectedChapterId === chapter.id
                        ? "bg-primary text-primary-foreground shadow-sm"
                        : "bg-muted/50 text-muted-foreground"
                    )}>
                      <BookOpen className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="text-[15px] font-semibold text-foreground block">{chapter.title}</span>
                      {chapter.description && (
                        <span className="text-xs text-muted-foreground block mt-1 leading-relaxed line-clamp-2">{chapter.description}</span>
                      )}
                    </div>
                    {selectedChapterId === chapter.id && (
                      <ChevronRight className="h-4 w-4 text-primary flex-shrink-0 mt-1" />
                    )}
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-sm text-muted-foreground bg-muted/5 rounded-2xl border border-dashed border-border/60">
                No chapters yet. Create your first one below.
              </div>
            )}

            <Button
              variant="ghost"
              onClick={onToggleCreateNew}
              className="w-full h-12 rounded-2xl border border-dashed border-border/80 hover:border-primary/40 hover:bg-primary/5 text-muted-foreground/80 hover:text-primary transition-all gap-2 text-xs font-semibold"
            >
              <Plus className="h-4 w-4" />
              Create new chapter instead
            </Button>
          </div>
        ) : (
          <div className="space-y-4 w-full max-w-xl mx-auto">
            <Input
              value={newChapterTitle}
              onChange={(e) => onNewChapterTitleChange(e.target.value)}
              placeholder="Chapter title (e.g. Chapter 1: The Beginning)"
              className="h-11 rounded-xl border-border bg-background text-sm"
            />
            <Textarea
              value={newChapterDescription}
              onChange={(e) => onNewChapterDescriptionChange(e.target.value)}
              placeholder="Brief description of the chapter — this will be used as context by the AI..."
              className="min-h-[100px] rounded-xl border-border bg-background text-sm resize-none leading-relaxed"
            />
            <Button
              variant="ghost"
              onClick={onToggleCreateNew}
              className="text-xs text-muted-foreground hover:text-foreground px-0 h-auto"
            >
              ← Select an existing chapter
            </Button>
          </div>
        )}
      </div>
    </motion.div>
  );
}
